import { Globe, UserPlus, Bot, Users, Puzzle, Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FoglioMenu, { type Voce } from './FoglioMenu';

/**
 * Il menu di «Gioca», il foglio che sale dal basso dalla voce centrale della
 * barra.
 *
 * E' un INDICE, non una porta: nessuna di queste sei righe costruisce la sua
 * destinazione: le apre soltanto. Il cablaggio — quale store, quale schermata,
 * quale foglio — sta tutto in App.tsx, l'unico posto che puo' cambiare
 * schermata, esattamente come fa gia' CustomTableHost per la partita
 * personalizzata.
 *
 * E' l'indice UNICO, a ogni larghezza. C'e' stato un tempo in cui montava solo
 * sul telefono: sul desktop le stesse sei porte erano tutte visibili nella hero
 * della dashboard, e la voce di barra andava dritta al lobby. Quelle pillole
 * erano questo menu ridetto in cima alla pagina — tre volte, fra hero della
 * dashboard, hero della landing e questo foglio — e sono andate via: le due hero
 * hanno un invito solo, «Gioca», e le porte stanno qui. Sul desktop il foglio
 * cambia forma, non contenuto (FoglioGioca.css).
 *
 * Nella testata ci sono solo il titolo e la X. C'era anche la pillola «N
 * Online», tolta di proposito (26 Set 2026) insieme a quella del lobby: il
 * numero sta gia' nella card della dashboard, e con pochi collegati — per lo
 * piu' bot di presenza contati fra gli ospiti — diceva «qui non c'e' nessuno»
 * proprio a chi stava per scegliere dove giocare.
 *
 * Del pannello qui non c'e' niente: la testata, il velo, il fuoco, Escape e il
 * gesto indietro stanno in FoglioMenu, che e' il vestito che questo foglio
 * condivide con quello degli Strumenti. Qui ci sono solo le sei righe, che sono
 * l'unica cosa che distingue un foglio dall'altro.
 */
interface Props {
  isOpen: boolean;
  onClose: () => void;
  onPlayOnline: () => void;
  onDirectChallenge: () => void;
  onPlayAI: () => void;
  onPlayInPerson: () => void;
  onDailyPuzzle: () => void;
  onTournaments: () => void;
  /**
   * La prima riga ASPETTA: la sessione per giocare online e' stata chiesta e non
   * e' ancora arrivata.
   *
   * Il foglio non sa che cos'e' un ospite — come non sa che cos'e' un torneo:
   * riceve lo stato dall'unico posto che puo' cambiare schermata (App.tsx) e lo
   * disegna. Stesso nome che la CTA della hero usa per la stessa attesa dietro
   * la stessa parola (`LandingSections`).
   */
  playPending?: boolean;
  /**
   * Perche' non ce l'ha fatta, come CHIAVE i18n: il messaggio inglese scritto
   * per i log non arriva a schermo. Va SOTTO la riga e non dentro — la riga deve
   * tornare premibile, e il motivo deve restare leggibile mentre si ripreme.
   */
  guestErrorKey?: string | null;
}

export default function FoglioGioca({
  isOpen,
  onClose,
  onPlayOnline,
  onDirectChallenge,
  onPlayAI,
  onPlayInPerson,
  onDailyPuzzle,
  onTournaments,
  playPending = false,
  guestErrorKey = null,
}: Props) {
  const { t } = useTranslation();

  /*
   * Le etichette ci sono gia' tutte, tradotte in tutte e otto le lingue: sono
   * le stesse parole che l'utente legge dove quelle porte stanno oggi — la hero
   * della dashboard, la riga «Avversario» della partita personalizzata, il
   * fondo delle pagine delle regole. Un menu che ribattezzasse le sue voci
   * sarebbe un menu che porta altrove.
   */
  const voci: Voce[] = [
    { id: 'online', icona: <Globe size={20} />, etichetta: t('rules.playOnline'), azione: onPlayOnline, primaria: true, serveRete: true, inCorso: playPending, erroreKey: guestErrorKey },
    { id: 'challenge', icona: <UserPlus size={20} />, etichetta: t('online.challenge.sendChallenge'), azione: onDirectChallenge, serveRete: true },
    { id: 'ai', icona: <Bot size={20} />, etichetta: t('dashboard.hero.ctaVsComputer'), azione: onPlayAI },
    { id: 'inperson', icona: <Users size={20} />, etichetta: t('lobby.opponentInPerson'), azione: onPlayInPerson },
    { id: 'puzzle', icona: <Puzzle size={20} />, etichetta: t('dashboard.hero.ctaDailyPuzzle'), azione: onDailyPuzzle, serveRete: true },
    /*
     * «Tornei», e per tutti.
     *
     * La riga stava dietro la modalita' sviluppatore — l'eredita' della voce di
     * barra, nascosta finche' i tornei non si fossero riempiti.
     *
     * Il nome e' quello corto. C'era stato un tentativo col nome per intero,
     * «Tornei e Arene», perche' dietro questa porta ci sono due cose: la lista
     * dei tornei e le arene in fondo a quella stessa pagina. A schermo pero' la
     * riga piu' lunga di tutte, per annunciare la seconda meta' di una pagina
     * che si vede scorrendo, costava piu' di quanto rendesse.
     */
    { id: 'tournaments', icona: <Trophy size={20} />, etichetta: t('nav.tournaments'), azione: onTournaments, serveRete: true },
  ];

  return (
    <FoglioMenu
      isOpen={isOpen}
      onClose={onClose}
      titolo={t('nav.play')}
      testId="foglio-gioca"
      voci={voci}
    />
  );
}
